/**
 * CHARGE CARD ON FILE — collect first, then send (Corbin's doctrine).
 *
 * One component, two placements: the Close Out review screen (banner directly
 * above the invoice block) and the invoice detail page. When the customer has
 * a saved card and the invoice is open with a balance due:
 *
 *   💳 Card on file: Visa ••7345 · profile: pay-on-invoice
 *   [ Charge $80.00 to card ]
 *
 * Success flips the chip to "✓ Paid $80.00 on card ••7345" (the invoice is
 * recorded paid synchronously server-side, so the send package re-renders as
 * a PAID receipt). A decline shows the bank's answer in plain words and the
 * next step — Send a new card link / Collect on site / Customer says the card
 * is fixed — and NO Retry (#1984: one try per card; "fixed" buys exactly one
 * more). Approve & Send stays available to send unpaid with a pay link.
 *
 * NO SILENT "hidden" (live incident 2026-07-16: invoice 072652639 stuck in
 * draft with no Charge button and no explanation). States that used to render
 * nothing now render a small informative chip:
 *   · draft + card     → "Draft — finalize to charge" + [Finalize]
 *   · draft + no card  → "Draft — send it to email a payment link, or
 *                         finalize first" + [Finalize]
 *   · open + no card   → "No card on file — payment link is on the sent
 *                         invoice" + [Copy payment link]
 * Finalize fires POST /invoices/:id/finalize (draft→open, NO email, never
 * auto-charges); a 409 TAX_STATUS_UNKNOWN swaps in the same amber
 * taxable/exempt set-and-continue interstitial the send dialog uses.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, CreditCard, FileCheck2, Info, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { DeclinedCardNextSteps, guardFromRefusal, type CardGuard } from "@/components/declined-card-next-steps";
import {
    CardHoldHowItWorks,
    HoldChip,
    fromHoldMessage,
    holdChargeWords,
    type ChargeHold,
    type FromHold,
} from "@/components/card-hold/card-hold";

export interface ChargeCardInvoice {
    id: string;
    invoiceNumber: string | null;
    status: string;
    /** Invoice total, dollars. */
    total: number;
    /** Sum of recorded payments, dollars. */
    paid: number;
    /** Latest stripe payment reference — marks a card-paid invoice. */
    lastStripeRef?: string | null;
}

export interface ChargeCardCustomer {
    customerId: string | null;
    customerName: string | null;
    hasCard: boolean;
    brand: string | null;
    last4: string | null;
    paymentProfile: string | null;
}

export type ChargeCardMode = "hidden" | "charge" | "paid" | "draft_card" | "draft_no_card" | "open_no_card";

/** What the panel should render for this invoice+card combination. */
export function chargeCardMode(invoice: ChargeCardInvoice, customer: ChargeCardCustomer): ChargeCardMode {
    // Drafts can't be charged yet — say so instead of rendering nothing, and
    // offer Finalize (draft→open, no email) as the way forward.
    if (invoice.status === "draft") return customer.hasCard ? "draft_card" : "draft_no_card";
    if (!customer.hasCard) {
        // Open with nothing to charge against: explain how payment arrives.
        if (invoice.status === "open" || invoice.status === "partially_paid") return "open_no_card";
        return "hidden";
    }
    if (invoice.status === "paid") {
        // Only claim "paid on card" when a Stripe payment actually landed.
        return invoice.lastStripeRef ? "paid" : "hidden";
    }
    if (invoice.status !== "open" && invoice.status !== "partially_paid") return "hidden";
    const balance = Math.round((invoice.total - invoice.paid) * 100) / 100;
    return balance > 0 ? "charge" : "hidden";
}

export const PROFILE_LABELS: Record<string, string> = {
    pay_on_invoice: "pay-on-invoice",
    auto_charge: "auto-charge",
    card_on_file_manual: "card on file (manual)",
    net_terms: "net terms",
};

export function cardLabel(customer: ChargeCardCustomer): string {
    const brand = customer.brand || "Card";
    return customer.last4 ? `${brand} ••${customer.last4}` : brand;
}

interface DeclineState {
    reason: string;
    failedPaymentId: string | null;
    /** #1984: what the server says about trying this card again. */
    guard: CardGuard | null;
}

export function ChargeCardPanel({
    invoice,
    customer,
    onSettled,
}: {
    invoice: ChargeCardInvoice;
    customer: ChargeCardCustomer;
    /** Called after any charge/retry attempt so the page can refetch. */
    onSettled?: () => void;
}) {
    const [decline, setDecline] = useState<DeclineState | null>(null);
    const [justCharged, setJustCharged] = useState<{ amount: number; last4: string | null } | null>(null);
    // TAX ENGINE V1: finalize refused with 409 TAX_STATUS_UNKNOWN — same amber
    // taxable/exempt set-and-continue interstitial as SendCustomerDialog.
    const [taxPrompt, setTaxPrompt] = useState<{ customerId: string } | null>(null);
    const [taxResolving, setTaxResolving] = useState<"taxable" | "exempt" | null>(null);

    const balance = Math.round((invoice.total - invoice.paid) * 100) / 100;

    // #1984: was this card already declined on this invoice? Then there is no
    // Charge button — the next step shows instead. Read before any click so a
    // reload doesn't bring the button back.
    const chargeable = chargeCardMode(invoice, customer) === "charge";
    const guardQ = useQuery({
        queryKey: ["invoice-charge-guard", invoice.id, customer.last4],
        queryFn: () =>
            api.get<{ hasCard: boolean; guard: CardGuard | null; hold?: ChargeHold | null }>(
                `/api/invoices/${invoice.id}/charge-guard`,
            ),
        enabled: chargeable,
    });

    const chargeMut = useMutation({
        mutationFn: () =>
            api.post<{ charged: boolean; amount: number; last4: string | null; fromHold?: FromHold | null }>(
                `/api/invoices/${invoice.id}/charge-card`,
                {},
            ),
        onSuccess: (r) => {
            setDecline(null);
            setJustCharged({ amount: r.amount, last4: r.last4 });
            // Card hold (#2037): say what came from the hold and what didn't.
            const f = r.fromHold ? fromHoldMessage(r.fromHold) : null;
            if (f && !f.ok) toast.error(f.text);
            else toast.success(f ? f.text : `Charged $${r.amount.toFixed(2)} to the card on file.`);
            onSettled?.();
        },
        onError: (e) => {
            const body = e instanceof ApiError ? (e.body as Record<string, unknown> | null) : null;
            const refusedGuard = guardFromRefusal(e);
            if ((e instanceof ApiError && e.status === 402 && body?.declined) || refusedGuard) {
                setDecline({
                    reason: String(body?.plainReason ?? body?.reason ?? "Card declined"),
                    failedPaymentId: typeof body?.failedPaymentId === "string" ? body.failedPaymentId : null,
                    guard: refusedGuard,
                });
                void guardQ.refetch();
                onSettled?.();
                return;
            }
            const msg = body && typeof body.message === "string" ? body.message : "The charge could not be started.";
            toast.error(msg);
            onSettled?.();
        },
    });

    // Finalize: draft → open with NO email (server mirrors the send flow's
    // tax snapshot / due date / pay-link side effects). After the refetch the
    // status prop flips to "open" and this panel re-renders as the charge
    // button (card on file) or the pay-link chip (no card).
    const finalizeMut = useMutation({
        mutationFn: () => api.post<{ invoice: { status: string } }>(`/api/invoices/${invoice.id}/finalize`, {}),
        onSuccess: () => {
            setTaxPrompt(null);
            toast.success("Invoice finalized — it is now open. No email was sent.");
            onSettled?.();
        },
        onError: (e) => {
            const body = e instanceof ApiError ? (e.body as Record<string, unknown> | null) : null;
            if (e instanceof ApiError && e.status === 409 && body?.error === "TAX_STATUS_UNKNOWN") {
                const cid = typeof body.customerId === "string" ? body.customerId : customer.customerId;
                if (cid) {
                    setTaxPrompt({ customerId: cid });
                    return;
                }
            }
            const msg = body && typeof body.message === "string" ? body.message : "The invoice could not be finalized.";
            toast.error(msg);
        },
    });

    /** One-click set-and-continue from the amber interstitial: persist the
     *  customer's tax status (the server re-taxes their draft docs), then
     *  retry the finalize. Same pattern as SendCustomerDialog. */
    const resolveTaxAndFinalize = async (status: "taxable" | "exempt") => {
        if (!taxPrompt) return;
        setTaxResolving(status);
        try {
            await api.patch(`/api/customers/${taxPrompt.customerId}`, { taxStatus: status });
            setTaxPrompt(null);
            finalizeMut.mutate();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : "Could not update the customer's tax status.");
        } finally {
            setTaxResolving(null);
        }
    };

    // Copy the no-login pay-page link (mints the idempotent token server-side
    // on first use) — the same endpoint the detail page's Copy Pay Link uses.
    const payLinkMut = useMutation({
        mutationFn: () => api.get<{ url: string }>(`/api/invoices/${invoice.id}/pay-link`),
        onSuccess: async (r) => {
            try {
                await navigator.clipboard.writeText(r.url);
                toast.success("Payment link copied to clipboard");
            } catch {
                toast.message("Payment link", { description: r.url });
            }
        },
        onError: (e) => {
            const body = e instanceof ApiError ? (e.body as Record<string, unknown> | null) : null;
            toast.error(body && typeof body.message === "string" ? body.message : "Could not create the payment link.");
        },
    });

    const mode = chargeCardMode(invoice, customer);
    if (mode === "hidden" && !justCharged) return null;

    const label = cardLabel(customer);
    const invLabel = invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : "invoice";

    // ✓ Paid chip — either freshly charged (before refetch) or loaded paid.
    if (justCharged || mode === "paid") {
        const amount = justCharged?.amount ?? invoice.paid;
        const last4 = justCharged?.last4 ?? customer.last4;
        return (
            <div
                data-testid="charge-card-paid-chip"
                className="flex items-center gap-2 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300"
            >
                <Check className="h-4 w-4 shrink-0" />
                <span className="font-medium">
                    Paid ${amount.toFixed(2)} on card {last4 ? `••${last4}` : "on file"}
                </span>
                <span className="text-emerald-700/70 dark:text-emerald-300/70">— invoice {invLabel}</span>
            </div>
        );
    }

    // Amber taxable/exempt set-and-continue (same pattern + styling as the
    // SendCustomerDialog interstitial) — shown when finalize 409'd on tax.
    const taxPromptBox = taxPrompt ? (
        <div
            data-testid="charge-card-tax-prompt"
            className="space-y-2 rounded-md border border-amber-400/60 bg-amber-50 p-3 dark:border-amber-500/50 dark:bg-amber-950/40"
        >
            <p className="flex items-start gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                Tax status not set — is this customer taxable or exempt?
            </p>
            <p className="text-xs text-amber-800/90 dark:text-amber-300/90">
                Picking one updates the customer, recalculates this invoice&apos;s tax, and finalizes it in one step.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
                <Button
                    type="button"
                    size="sm"
                    className="h-8 bg-amber-700 text-white hover:bg-amber-800"
                    disabled={taxResolving !== null || finalizeMut.isPending}
                    onClick={() => void resolveTaxAndFinalize("taxable")}
                >
                    {taxResolving === "taxable" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                    Taxable — apply tax &amp; finalize
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 border-amber-500/60"
                    disabled={taxResolving !== null || finalizeMut.isPending}
                    onClick={() => void resolveTaxAndFinalize("exempt")}
                >
                    {taxResolving === "exempt" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                    Tax exempt — finalize as-is
                </Button>
            </div>
        </div>
    ) : null;

    // Draft chips: the invoice is not billable yet — say so and offer the way
    // forward instead of the old silent nothing (the 072652639 incident).
    if (mode === "draft_card" || mode === "draft_no_card") {
        return (
            <div className="space-y-2">
                <div
                    data-testid="charge-card-draft-chip"
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm"
                >
                    <span className="flex min-w-0 items-center gap-2">
                        <Info className="h-4 w-4 shrink-0 text-foreground/60" />
                        <span className="min-w-0">
                            {mode === "draft_card" ? (
                                <>
                                    <span className="font-medium">Draft</span> — finalize to charge the card on file (
                                    {label}). Finalizing opens the invoice without emailing anyone.
                                </>
                            ) : (
                                <>
                                    <span className="font-medium">Draft</span> — send it to email a payment link, or
                                    finalize first. No card on file to charge.
                                </>
                            )}
                        </span>
                    </span>
                    <Button
                        size="sm"
                        variant="outline"
                        data-testid="finalize-invoice-button"
                        disabled={finalizeMut.isPending || taxResolving !== null}
                        onClick={() => finalizeMut.mutate()}
                    >
                        {finalizeMut.isPending ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <FileCheck2 className="mr-1 h-3.5 w-3.5" />
                        )}
                        Finalize
                    </Button>
                </div>
                {taxPromptBox}
            </div>
        );
    }

    // Open with no card on file: the money arrives through the pay link on
    // the emailed invoice — say so, and offer to copy that link directly.
    if (mode === "open_no_card") {
        return (
            <div
                data-testid="charge-card-nocard-chip"
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm"
            >
                <span className="flex min-w-0 items-center gap-2">
                    <Info className="h-4 w-4 shrink-0 text-foreground/60" />
                    <span className="min-w-0">No card on file — payment link is on the sent invoice.</span>
                </span>
                <Button
                    size="sm"
                    variant="outline"
                    data-testid="copy-pay-link-button"
                    disabled={payLinkMut.isPending}
                    onClick={() => payLinkMut.mutate()}
                >
                    {payLinkMut.isPending ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <Link2 className="mr-1 h-3.5 w-3.5" />
                    )}
                    Copy payment link
                </Button>
            </div>
        );
    }

    const fetchedGuard = guardQ.data?.guard ?? null;
    const guardSource = fetchedGuard ?? decline?.guard ?? null;
    // Card hold (#2037, step 5): taking from a live hold isn't a new try on the card.
    const hold = guardQ.data?.hold ?? null;
    const liveHold = hold?.state === "held";
    const activeGuard = guardSource && guardSource.state !== "clear" && !liveHold ? guardSource : null;
    const words = holdChargeWords(Math.round(balance * 100), hold);

    const doCharge = async () => {
        const ok = await confirmDialog({
            title: "Charge card on file",
            message: words.note
                ? `${words.note} Go ahead for ${customer.customerName ?? "this customer"}?`
                : `Charge $${balance.toFixed(2)} to ${customer.customerName ?? "this customer"}'s ${label}?`,
            confirmLabel: words.button ?? `Charge $${balance.toFixed(2)}`,
        });
        if (!ok) return;
        chargeMut.mutate();
    };

    return (
        <div
            data-testid="charge-card-panel"
            className="space-y-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2"
        >
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 shrink-0 text-primary" />
                    <span className="font-medium">Card on file: {label}</span>
                    {hold && (
                        <HoldChip state={hold.state === "held" ? "held" : "expired"} amountCents={hold.amountCents} />
                    )}
                    {customer.paymentProfile && (
                        <span className="text-foreground/60">
                            · profile:{" "}
                            {customer.customerId ? (
                                <Link
                                    to={`/customers/${customer.customerId}`}
                                    className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                                    title="Open the customer's payment profile — flip to auto-charge to collect future invoices automatically"
                                >
                                    {PROFILE_LABELS[customer.paymentProfile] ?? customer.paymentProfile}
                                </Link>
                            ) : (
                                (PROFILE_LABELS[customer.paymentProfile] ?? customer.paymentProfile)
                            )}
                        </span>
                    )}
                </span>
                {activeGuard?.state === "blocked" ? (
                    <span className="text-xs text-muted-foreground" data-testid="charge-card-stopped">
                        Not charging this card again
                    </span>
                ) : activeGuard?.state === "one_more_try" ? null : (
                    <Button
                        size="sm"
                        data-testid="charge-card-button"
                        disabled={chargeMut.isPending}
                        onClick={() => void doCharge()}
                    >
                        {chargeMut.isPending ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <CreditCard className="mr-1 h-3.5 w-3.5" />
                        )}
                        {words.button ?? `Charge $${balance.toFixed(2)} to card`}
                    </Button>
                )}
            </div>
            {words.note && (
                <p className="flex items-center gap-2 text-xs text-foreground/80" data-testid="charge-hold-note">
                    {words.note}
                    <CardHoldHowItWorks />
                </p>
            )}
            <p className="text-xs text-primary/80" data-testid="charge-card-hint">
                Charges the saved card now — this moves money. To log a payment collected elsewhere, use “Mark Paid”.
            </p>
            {activeGuard ? (
                <DeclinedCardNextSteps
                    guard={activeGuard}
                    last4={customer.last4}
                    customerId={customer.customerId}
                    onChanged={() => {
                        void guardQ.refetch();
                        onSettled?.();
                    }}
                    onTryOnceMore={() => void doCharge()}
                    tryPending={chargeMut.isPending}
                />
            ) : (
                decline && (
                    <div
                        data-testid="charge-card-decline"
                        className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                    >
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span className="min-w-0">
                            Card declined: {decline.reason} Approve &amp; Send delivers the invoice unpaid with a pay
                            link.
                        </span>
                    </div>
                )
            )}
        </div>
    );
}
